import React, {
  useRef,
  useState,
  memo,
  useEffect,
  useMemo,
  ReactElement,
  ReactNode,
} from 'react';
import { useIntl } from 'umi';
import { useDebounceEffect, useDocumentVisibility } from 'ahooks';
import { Bubble, Alert, Button, Text } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import sanitizeHtml from 'sanitize-html';
import ChartMessage from '@/pages/datalab/pages/BehaviorAnalysis/GptAnalysis/Chat/components/ChartMessage/index';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import breaks from 'remark-breaks';
import ReactMarkdown from 'react-markdown';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { isEmpty } from 'lodash-es';
import './Message.scss';

export interface MessageProps {
  type:
  | 'text'
  | 'custom'
  | 'finish'
  | 'chart'
  | 'sql'
  | 'error'
  | 'summary'
  | 'plans'; // custom可以自定义消息的渲染方式, 搭配renderMessage方法一起使用
  position?: 'left' | 'right';
  content: any;
  renderMessage?: (message: MessageProps) => void;
  className?: string;
  feedbackOptions?: {
    handleFeedback: (likeState: string) => void;
  }; // 点赞点踩配置
  showFeedback?: boolean; // 是否显示点点踩
  onElementRender?: (...args: any[]) => void; // 强制其他组件进行渲染，获取到element对象的渲染状态
}

export default memo(function Message(props: MessageProps) {
  const {
    type = 'text',
    position = 'left',
    className = '',
    showFeedback = true,
    feedbackOptions,
    onElementRender,
    ...otherProps
  } = props;
  const element = MESSAGE_TYPE_MAP[type];

  return (
    <div
      className={`dbl-message ${position} gpt-${element ? type : 'error'
      } ${className}`}
    >
      <div className={'dbl-message-main'}>
        <div className={'dbl-message-body'}>
          {element ? (
            React.createElement(element, { ...otherProps, onElementRender })
          ) : (
            <ErrorMessage content={'消息类型错误, 无法显示'} state={'error'} />
          )}
        </div>
      </div>
      <div className={'dbl-message-right'}>
        {showFeedback && feedbackOptions && (
          <FeedbackLikes {...feedbackOptions} />
        )}
      </div>
    </div>
  );
});

const MESSAGE_TYPE_MAP: any = {
  text: NormalText,
  chart: ChartMessage,
  plans: PlanMessage,
  // sql: SQLMessage,
  summary: SummaryMessage,
  error: ErrorMessage,
};
// 普通文本
function NormalText(props) {
  const {
    content = '',
    status,
    className = '',
    type,
    command,
    isStreaming,
    question,
    references = []
  } = props;
  const documentVisibility = useDocumentVisibility();
  const { formatMessage } = useIntl();
  const { dblFormatMessage: gptFormatMessage } = useDblIntl('gpt');
  const hiddenDomScreenRef = useRef(false);
  const isInternet = !isEmpty(command) && isStreaming;
  const isQuery = status === 'end';
  useEffect(() => {
    if (documentVisibility === 'hidden') {
      hiddenDomScreenRef.current = true;
      return;
    }
    return () => {
      hiddenDomScreenRef.current = false;
    };
  }, [documentVisibility]);
  const ThinkIngContent = useMemo(() => {
    return () => {
      // 如果返回的类型不是正常的，统一吧thinking移除
      if (['warning', 'error'].includes(type) && hiddenDomScreenRef.current) {
        return '';
      }
      return !isQuery ? (
        <div className={'plan-query-thinking'}>
          <LoadingIcon />
          {formatMessage({ id: 'dbl_gpt_message_query_thinking' })}
        </div>
      ) : (
        ''
      );
    };
  }, [formatMessage, isQuery, type]);
  const metricsLinkData = useMemo(() => {
    if (isEmpty(references)) return [];
    return references?.map((task: { title: string; href: string; }) => ({
      name: task.title,
      url: task.href
    }));
  }, [references]);
  return (
    <div className={className}>
      {isInternet && (
        <div className="markdown-text">
          {!isQuery ? (
            <LoadingIcon />
          ) : (
            <SvgIcon type={'circle-success'} className={'success-icon'} />
          )}
          {gptFormatMessage('search_online')} : {question}
        </div>
      )}
      <ReactMarkdown
        className={'markdown-message'}
        children={sanitizeHtml(content, {
          allowedAttributes: { div: ['class'], a: ['href', 'target'] },
        })}
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[gfm, breaks]}
      />
      {isInternet && isQuery && !isEmpty(metricsLinkData) && (
        <MultipleGameMetricsLink
          {...{
            list: metricsLinkData,
            gptFormatMessage,
            isLink: false
          }}
        />
      )}
      <ThinkIngContent />
    </div>
  );
}


function ErrorMessage(props) {
  const { content = '', status = 'end', state } = props;
  return (
    <Alert type={state} className={`dbl-message-${state} dbl-message-warn`}>
      <NormalText content={content} status={status} type={state} />
    </Alert>
  );
}

// 查询计划信息
function PlanMessage(props) {
  const { status, content = '', onElementRender } = props;
  const { formatMessage } = useIntl();
  const times: any = useRef();
  // 为了实现消息的展开折叠过渡效果, 需要获取隐藏元素的实际高度, 在折叠展开过程中重置height为具体数值, 才能有过渡效果
  const [height, setHeight] = useState<any>('auto');
  const isQuery = status !== 'end';
  const collapseRef = useRef(null);
  const isCollapse = height === 0;
  useDebounceEffect(
    () => {
      times.current = setTimeout(() => {
        if (status === 'start' || status === 'process') return;
        // 默认是auto时，需要隐藏，其他对话取他们本身的高度。防止自动折叠全部同步关闭,
        // 如果是在拉取数据中，请求时间过长，会导致防抖过效,于是当状态处于loading中，不着急自动关闭，先让他等待，
        setHeight(height === 'auto' ? (props?.showThinking ? height : 0) : height);
      }, 100);

      return () => {
        clearTimeout(times.current);
      };
    },
    [onElementRender],
    {
      wait: 500,
    },
  );
  return (
    <>
      <Button
        className={'plan-query-btn plan-rectify-btn'}
        onClick={() => {
          setHeight(
            isCollapse
              ? isQuery
                ? '100%'
                : collapseRef?.current?.offsetHeight + 14
              : 0,
          );
        }}
      >
        {isQuery ? (
          <LoadingIcon />
        ) : (
          <SvgIcon type={'circle-success'} className={'success-icon'} />
        )}
        {formatMessage({
          id: isQuery ? 'dbl_gpt_query' : 'dbl_gpt_query_complete',
        })}
        <SvgIcon
          type={isCollapse ? 'arrow-down' : 'arrow-up'}
          className={'arrow-icon'}
        />
      </Button>
      <div
        className={'plan-query-collapse'}
        style={{
          height: height,
          overflow: 'hidden',
        }}
      >
        <div className={'plan-query-list'} ref={collapseRef}>
          <NormalText content={content} status={status} />
        </div>
      </div>
    </>
  );
}

// 摘要总结消息
function SummaryMessage(props) {
  return (
    <>
      <MessageTitle title={props.title} icon={props.icon} />
      <NormalText
        content={props.content}
        status={props.status}
        className={'summary-content'}
      />
    </>
  );
}

// 继续提示语
// function ContinueInfo() {
// 	const {formatMessage} = useIntl();
// 	return (
// 		<div className={"continue-info"}>
// 			{
// 				formatMessage({id: "dbl_gpt_message_continue"})
// 			}
// 		</div>

// 点赞点踩功能
function FeedbackLikes({ handleFeedback, hoverShow = false }) {
  const { formatMessage } = useIntl();
  const feedbackRef = useRef(null);
  const [likeState, setLikeState] = useState<string>('');
  const iconClick = (value: string) => {
    if (value === likeState) return;
    handleFeedback?.(value);
    setLikeState(value);
  };

  return (
    <div
      ref={feedbackRef}
      className={`feedback ${hoverShow ? 'hover-show' : ''} ${likeState ? 'hover-show-always' : ''
      }`}
    >
      <Bubble
        popupContainer={feedbackRef.current}
        dark
        arrowPointAtCenter
        content={formatMessage({
          id: 'dbh_feedback_isgod_tips',
        })}
        trigger="click"
        closeOnScroll
      >
        <i
          onClick={() => iconClick('like')}
          className={`feedback-normal ${likeState === 'like' ? 'feedback-action' : ''
          }`}
        >
          <SvgIcon
            className="feedback-icon"
            type={likeState === 'like' ? 'like-all' : 'very-good'}
          />
        </i>
      </Bubble>
      <Bubble
        popupContainer={feedbackRef.current}
        dark
        arrowPointAtCenter
        content={formatMessage({
          id: 'dbh_feedback_isgod_tips',
        })}
        trigger="click"
        closeOnScroll
      >
        <i
          onClick={() => iconClick('unlike')}
          className={`feedback-normal ${likeState === 'unlike' ? 'feedback-action' : ''
          }`}
        >
          <SvgIcon
            className="feedback-icon"
            type={likeState === 'unlike' ? 'no-like-all' : 'not-work'}
          />
        </i>
      </Bubble>
    </div>
  );
}

// 回复消息的标题
function MessageTitle(props) {
  return (
    <div className={'dbl-message-title'}>
      {props.icon && <SvgIcon type={props.icon} />}
      <span dangerouslySetInnerHTML={{ __html: props.title }} />
    </div>
  );
}

function LoadingIcon() {
  return (
    <span className={'tea-action-state'}>
      <i className={'tea-mr-2n tea-icon tea-icon-loading'}></i>
    </span>
  );
}

// 等待机器回复提示语
export function Thinking({ content = '' }) {
  return (
    <div className={'thinking dbl-message dbl-message-main'}>{content}</div>
  );
}
// 图表头部跳转
export const ChartLink = <S extends string>({
  chartUrl,
  chartTitle,
  gptFormatMessage,
}: {
  chartUrl: S;
  chartTitle: S;
  gptFormatMessage: (...args: any[]) => ReactNode;
}): ReactElement => {
  if (chartUrl) {
    return (
      <Bubble content={gptFormatMessage('gpt_link')} dark>
        <a href={chartUrl} target={'_blank'} className={'chart-url'}>
          <span dangerouslySetInnerHTML={{ __html: chartTitle }} className='chart-url_title' />
          &nbsp;
          <SvgIcon className={'icon-arrow-right'} type={'arrow-right'} />
        </a>
      </Bubble>
    );
  } else {
    return <span dangerouslySetInnerHTML={{ __html: chartTitle }} className='chart-url_title' />;
  }
};

// 多指标跳转链接
export const MultipleGameMetricsLink = memo(
  ({
    list = [],
    gptFormatMessage,
    isLink = true
  }: {
    list: Array<{ [key in string]: string }>;
    gptFormatMessage: (...args: any[]) => ReactNode;
    isLink?: boolean
  }) => {
    // 跳转lida项目
    const handleOpenLidaProject = (): void => {
      window.open(window.location.origin + '/' + 'v2/aigc/Analysis');
    };
    // 多指标路径跳转
    const handleOpenGameLink = (
      url: string,
    ): ReturnType<typeof handleOpenLidaProject> => {
      if (!url) return;
      // 这里的url,后端会返回本系统的URL,以及第三方页面的url,这里跳转需要判断一下
      const isHttp = url.startsWith('http');
      const openLinkUrl = isHttp ? url : window.location.origin + '/' + url;
      window.open(openLinkUrl);
    };
    return (
      <>
        <div className="more-split-line" />
        <div className="more-split-container">
          <SvgIcon
            type={'information-icon'}
            className={'more-split-container-icon cover-icon'}
          />
          <span className="more-split-container-title">
            {gptFormatMessage('information_source')}
          </span>
          <div className="more-split-container-list">
            {list?.map((task) => (
              <div
                key={task.name}
                className="more-split-container-list-item"
                onClick={() => handleOpenGameLink(task.url)}
              >
                <Text parent="div" style={{ maxWidth: 240 }} overflow tooltip>
                  <span className="more-split-container-list-name">
                    {task.name}
                  </span>
                </Text>
                <SvgIcon
                  type={'aigc-jump'}
                  className={'more-split-container-icon'}
                />
              </div>
            ))}
          </div>
          {
            isLink && <div className="more-split-container-openUrl">
              {gptFormatMessage('more_analysis')}
              <span
                className="more-split-container-openUrl-name"
                onClick={handleOpenLidaProject}
              >
                {gptFormatMessage('tool_try')}
              </span>
            </div>
          }
        </div>
      </>
    );
  },
);

// prompt推荐例子
export const PromptRecommendationExample = memo(<T extends { [key in string]: string },>({ list = [], handleOnChangText }: { list?: T[], handleOnChangText: (...args: any[]) => void }): ReactElement => {
  const {
    dblFormatMessage: gptFormatMessage,
    isEn,
  } = useDblIntl('gpt');
  return (
    <div className='recommendation-container'>
      <div className='recommendation-container-title'>
        {!isEmpty(list) && gptFormatMessage('Keep_asking_questions')}
      </div>
      <div className='recommendation-container-list'>
        {
          list.map(item =>
            <div key={item.en_query} className='recommendation-container-list-item' onClick={() => handleOnChangText?.(isEn ? item.en_query : item.cn_query)}>
              {isEn ? item.en_query: item.cn_query}
            </div>
          )
        }
      </div>
    </div>
  );
});